/*
 * LD Sticky winkelknop (sections/ld-sticky-atc.liquid)
 * Vanilla JS, no dependencies. Loaded with defer after Dawn's constants.js / pubsub.js.
 */
(function () {
  'use strict';

  if (!('customElements' in window) || customElements.get('ld-sticky-atc')) return;

  var SCROLL_LOCK_CLASS = /(^|\s)overflow-hidden(-mobile|-tablet|-desktop)?(\s|$)/;
  var TEXT_INPUT_SELECTOR =
    'input:not([type="radio"]):not([type="checkbox"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="hidden"]):not([type="range"]):not([type="color"]), textarea, select, [contenteditable="true"]';
  var reducedMotionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function splitList(value) {
    return String(value || '')
      .split(',')
      .map(function (part) {
        return part.trim();
      })
      .filter(Boolean);
  }

  function safeQuery(root, selector) {
    try {
      return root.querySelector(selector);
    } catch (error) {
      return null;
    }
  }

  // First element that matches, honouring the order in which the selectors were written.
  function firstMatch(roots, selectors) {
    for (var i = 0; i < selectors.length; i++) {
      for (var j = 0; j < roots.length; j++) {
        if (!roots[j]) continue;
        var el = safeQuery(roots[j], selectors[i]);
        if (el) return el;
      }
    }
    return null;
  }

  function hasSize(el) {
    if (!el || !el.isConnected) return false;
    var rect = el.getBoundingClientRect();
    return rect.width > 0 || rect.height > 0;
  }

  // True when an element is actually painted inside the viewport (open drawer / overlay).
  function isShown(el) {
    if (!el || !el.getClientRects().length) return false;
    var style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity) < 0.05) return false;
    var rect = el.getBoundingClientRect();
    return (
      rect.width > 0 &&
      rect.height > 0 &&
      rect.bottom > 0 &&
      rect.right > 0 &&
      rect.top < window.innerHeight &&
      rect.left < window.innerWidth
    );
  }

  function prefersReducedMotion() {
    return !!(reducedMotionQuery && reducedMotionQuery.matches);
  }

  class LdStickyAtc extends HTMLElement {
    connectedCallback() {
      this.cleanups = [];
      this.timers = [];
      this.visible = false;
      this.state = {
        triggerOut: false, // main add-to-cart button scrolled out of view
        appCartOpen: false, // UpCart API or custom open/close events
        inputFocused: false, // mobile keyboard open
        forced: false, // theme editor: section selected
      };

      this.mode = this.dataset.mode === 'add' ? 'add' : 'scroll';
      this.showBefore = this.dataset.showBefore === 'true';
      this.highlight = this.dataset.highlight !== 'false';
      this.hideSelectors = splitList(this.dataset.hideSelectors);

      this.button = this.querySelector('[data-ld-sticky-button]');
      this.label = this.querySelector('[data-ld-sticky-label]');
      this.priceEl = this.querySelector('[data-ld-sticky-price]');
      this.defaultLabel = this.label ? this.label.getAttribute('data-default-label') || this.label.textContent.trim() : '';

      this.productRoot =
        document.querySelector('product-info[id^="MainProduct-"]') || document.querySelector('[id^="MainProduct-"]');
      this.mainSectionId = this.productRoot
        ? this.productRoot.dataset.section || this.productRoot.id.replace('MainProduct-', '')
        : null;

      var roots = [this.productRoot, document];
      this.atcButton = firstMatch(roots, splitList(this.dataset.atcSelector));
      this.trigger = this.findTrigger();

      this.onButtonClick = this.onButtonClick.bind(this);
      this.update = this.update.bind(this);
      if (this.button) this.button.addEventListener('click', this.onButtonClick);

      this.observeTrigger();
      this.observeBlockers();
      this.observeInputs();
      this.observePrice();
      this.observeAtcState();
      this.hookUpcart();
      this.hookCustomEvents();
      this.hookThemeEditor();
      this.reserveSpace();
      this.update();
    }

    disconnectedCallback() {
      (this.cleanups || []).forEach(function (fn) {
        try {
          fn();
        } catch (error) {
          /* ignore */
        }
      });
      (this.timers || []).forEach(function (id) {
        clearTimeout(id);
      });
      this.cleanups = [];
      this.timers = [];
      if (this.button) this.button.removeEventListener('click', this.onButtonClick);
      if (!document.querySelector('ld-sticky-atc')) {
        document.body.classList.remove('ld-sticky-atc-reserve');
        document.documentElement.style.removeProperty('--ld-sticky-atc-height');
      }
    }

    later(fn, ms) {
      var timers = this.timers;
      var id = setTimeout(function () {
        var index = timers.indexOf(id);
        if (index > -1) timers.splice(index, 1);
        fn();
      }, ms);
      timers.push(id);
      return id;
    }

    /* ---------- Trigger: main add-to-cart button ---------- */

    findTrigger() {
      if (hasSize(this.atcButton)) return this.atcButton;
      // Fallbacks when the button is missing or hidden by an app.
      var form = this.atcButton ? this.atcButton.closest('product-form') : null;
      if (hasSize(form)) return form;
      var target = this.findScrollTarget();
      if (hasSize(target)) return target;
      return this.atcButton || null;
    }

    observeTrigger() {
      if (!this.trigger || !('IntersectionObserver' in window)) return;
      var self = this;
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var rect = entry.boundingClientRect;
          if (rect.width === 0 && rect.height === 0) {
            // Hidden trigger (display: none): stay hidden, never guess.
            self.state.triggerOut = false;
          } else if (entry.isIntersecting) {
            self.state.triggerOut = false;
          } else {
            // rootBounds can be null inside the theme editor iframe, the viewport top is 0.
            self.state.triggerOut = self.showBefore || rect.bottom <= 0;
          }
        });
        self.update();
      });
      observer.observe(this.trigger);
      this.cleanups.push(function () {
        observer.disconnect();
      });
    }

    /* ---------- Things that must hide the bar ---------- */

    isBlocked() {
      if (this.state.appCartOpen || this.state.inputFocused) return true;
      if (SCROLL_LOCK_CLASS.test(document.body.className)) return true; // Dawn cart drawer, menu drawer, modals
      if (SCROLL_LOCK_CLASS.test(document.documentElement.className)) return true;
      for (var i = 0; i < this.hideSelectors.length; i++) {
        var el = safeQuery(document, this.hideSelectors[i]);
        if (el && !this.contains(el) && isShown(el)) return true;
      }
      return false;
    }

    observeBlockers() {
      var self = this;
      var recheck = function () {
        self.update();
      };

      // Scroll locks are set as classes/styles on <html> and <body>.
      var mo = new MutationObserver(recheck);
      mo.observe(document.body, { attributes: true, attributeFilter: ['class', 'style'] });
      mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'style'] });
      this.cleanups.push(function () {
        mo.disconnect();
      });

      // Drawers usually open after a click/tap/keypress or after a cart update: re-check shortly after.
      var delayed = function () {
        self.tryHookUpcart();
        self.later(recheck, 80);
        self.later(recheck, 500);
        self.later(recheck, 1200);
      };
      ['click', 'keyup', 'touchend'].forEach(function (type) {
        document.addEventListener(type, delayed, { passive: true, capture: true });
        self.cleanups.push(function () {
          document.removeEventListener(type, delayed, { passive: true, capture: true });
        });
      });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        var unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, delayed);
        this.cleanups.push(unsubscribeCart);
      }
    }

    observeInputs() {
      var self = this;
      var onFocusIn = function (event) {
        var target = event.target;
        if (!target || !target.matches || self.contains(target)) return;
        if (target.matches(TEXT_INPUT_SELECTOR)) {
          self.state.inputFocused = true;
          self.update();
        }
      };
      var onFocusOut = function () {
        self.later(function () {
          var active = document.activeElement;
          self.state.inputFocused = !!(active && active.matches && active.matches(TEXT_INPUT_SELECTOR));
          self.update();
        }, 60);
      };
      document.addEventListener('focusin', onFocusIn);
      document.addEventListener('focusout', onFocusOut);
      this.cleanups.push(function () {
        document.removeEventListener('focusin', onFocusIn);
        document.removeEventListener('focusout', onFocusOut);
      });
    }

    hookUpcart() {
      // UpCart public API: window.upcartSubscribeCartOpened / upcartSubscribeCartClosed.
      // UpCart loads asynchronously, so poll for ~20 s and retry on every interaction afterwards.
      var self = this;
      var tries = 0;
      var poll = function () {
        if (!self.isConnected || self.tryHookUpcart()) return;
        tries += 1;
        if (tries < 40) self.later(poll, 500);
      };
      poll();
    }

    tryHookUpcart() {
      if (this.upcartHooked) return true;
      if (
        typeof window.upcartSubscribeCartOpened !== 'function' ||
        typeof window.upcartSubscribeCartClosed !== 'function'
      ) {
        return false;
      }
      var self = this;
      this.upcartHooked = true;
      try {
        window.upcartSubscribeCartOpened(function () {
          if (!self.isConnected) return;
          self.state.appCartOpen = true;
          self.update();
        });
        window.upcartSubscribeCartClosed(function () {
          if (!self.isConnected) return;
          self.state.appCartOpen = false;
          self.update();
        });
      } catch (error) {
        /* API changed: selectors + scroll-lock detection still apply */
      }
      return true;
    }

    hookCustomEvents() {
      var self = this;
      var onOpen = function () {
        self.state.appCartOpen = true;
        self.update();
      };
      var onClose = function () {
        self.state.appCartOpen = false;
        self.update();
      };
      splitList(this.dataset.openEvents).forEach(function (name) {
        window.addEventListener(name, onOpen);
        document.addEventListener(name, onOpen);
        self.cleanups.push(function () {
          window.removeEventListener(name, onOpen);
          document.removeEventListener(name, onOpen);
        });
      });
      splitList(this.dataset.closeEvents).forEach(function (name) {
        window.addEventListener(name, onClose);
        document.addEventListener(name, onClose);
        self.cleanups.push(function () {
          window.removeEventListener(name, onClose);
          document.removeEventListener(name, onClose);
        });
      });
    }

    hookThemeEditor() {
      if (!window.Shopify || !window.Shopify.designMode) return;
      var self = this;
      var sectionId = this.dataset.sectionId;
      var onSelect = function (event) {
        if (event.detail && event.detail.sectionId === sectionId) {
          self.state.forced = true;
          self.update();
        }
      };
      var onDeselect = function (event) {
        if (event.detail && event.detail.sectionId === sectionId) {
          self.state.forced = false;
          self.update();
        }
      };
      document.addEventListener('shopify:section:select', onSelect);
      document.addEventListener('shopify:section:deselect', onDeselect);
      this.cleanups.push(function () {
        document.removeEventListener('shopify:section:select', onSelect);
        document.removeEventListener('shopify:section:deselect', onDeselect);
      });
    }

    /* ---------- Visibility ---------- */

    update() {
      if (!this.isConnected) return;
      var show = this.state.forced || (!!this.trigger && this.state.triggerOut && !this.isBlocked());
      if (show === this.visible) return;
      this.visible = show;
      this.classList.toggle('is-visible', show);
      if (show) {
        this.removeAttribute('inert');
      } else {
        this.setAttribute('inert', '');
      }
    }

    reserveSpace() {
      var self = this;
      var apply = function () {
        var height = self.offsetHeight; // 0 when hidden via CSS (e.g. desktop disabled)
        document.documentElement.style.setProperty('--ld-sticky-atc-height', height + 'px');
        document.body.classList.toggle('ld-sticky-atc-reserve', height > 0 && !!self.trigger);
      };
      apply();
      if ('ResizeObserver' in window) {
        var ro = new ResizeObserver(apply);
        ro.observe(this);
        this.cleanups.push(function () {
          ro.disconnect();
        });
      } else {
        window.addEventListener('resize', apply);
        this.cleanups.push(function () {
          window.removeEventListener('resize', apply);
        });
      }
    }

    /* ---------- Price ---------- */

    getMainPrice() {
      if (this.mainSectionId) {
        var byId = document.getElementById('price-' + this.mainSectionId);
        if (byId) return byId;
      }
      return this.productRoot ? this.productRoot.querySelector('[id^="price-"]') : null;
    }

    syncPriceFrom(source) {
      if (!this.priceEl || !source) return;
      var price = source.querySelector('.price');
      if (!price) return;
      var clone = price.cloneNode(true);
      clone.classList.remove('price--large', 'price--medium', 'price--show-badge');
      clone.querySelectorAll('.badge, [id]').forEach(function (node) {
        if (node.classList.contains('badge')) {
          node.remove();
        } else {
          node.removeAttribute('id');
        }
      });
      this.priceEl.innerHTML = '';
      this.priceEl.appendChild(clone);
      this.priceEl.hidden = source.classList.contains('hidden');
    }

    fetchPrice(variantId) {
      // Fallback when the main price block is not on the page: Section Rendering API of this section.
      if (!this.priceEl || !variantId || !window.fetch) return;
      var self = this;
      var url =
        window.location.pathname +
        '?variant=' +
        encodeURIComponent(variantId) +
        '&section_id=' +
        encodeURIComponent(this.dataset.sectionId);
      fetch(url)
        .then(function (response) {
          return response.ok ? response.text() : Promise.reject(response.status);
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-ld-sticky-price]');
          if (fresh && self.priceEl) {
            self.priceEl.innerHTML = fresh.innerHTML;
            self.priceEl.hidden = false;
          }
        })
        .catch(function () {
          /* keep the last known price */
        });
    }

    observePrice() {
      if (!this.priceEl) return;
      var self = this;
      var mainPrice = this.getMainPrice();

      if (mainPrice) {
        this.syncPriceFrom(mainPrice);
        var pending = false;
        var mo = new MutationObserver(function () {
          if (pending) return;
          pending = true;
          self.later(function () {
            pending = false;
            self.syncPriceFrom(self.getMainPrice());
          }, 30);
        });
        mo.observe(mainPrice, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['class'],
        });
        this.cleanups.push(function () {
          mo.disconnect();
        });
      }

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        var unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
          var data = event && event.data;
          if (!data || !self.isConnected) return;
          if (self.mainSectionId && data.sectionId && data.sectionId !== self.mainSectionId) return;
          var fromHtml =
            data.html && typeof data.html.getElementById === 'function'
              ? data.html.getElementById('price-' + data.sectionId)
              : null;
          if (fromHtml) {
            self.syncPriceFrom(fromHtml);
          } else if (!self.getMainPrice() && data.variant) {
            self.fetchPrice(data.variant.id);
          }
        });
        this.cleanups.push(unsubscribe);
      }
    }

    /* ---------- Button ---------- */

    observeAtcState() {
      if (this.mode !== 'add' || !this.atcButton || !this.button) return;
      var self = this;
      var sync = function () {
        var atc = self.atcButton;
        var disabled = atc.hasAttribute('disabled');
        self.button.disabled = disabled;
        self.button.setAttribute('aria-busy', atc.classList.contains('loading') ? 'true' : 'false');
        if (self.label) {
          var mainLabel = atc.querySelector('span');
          var mainText = mainLabel ? mainLabel.textContent.trim() : '';
          self.label.textContent = disabled && mainText ? mainText : self.defaultLabel;
        }
      };
      sync();
      var mo = new MutationObserver(sync);
      mo.observe(this.atcButton, { attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'] });
      this.cleanups.push(function () {
        mo.disconnect();
      });

      // Dawn shows add-to-cart errors inside the product form, which is off-screen at this point: bring it into view.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartError) {
        var unsubscribeError = subscribe(PUB_SUB_EVENTS.cartError, function () {
          if (!self.isConnected || !self.lastAddClick || Date.now() - self.lastAddClick > 15000) return;
          self.lastAddClick = 0;
          var form = self.atcButton.closest('product-form') || self.atcButton;
          self.later(function () {
            self.scrollToTarget(form, { focus: false });
          }, 50);
        });
        this.cleanups.push(unsubscribeError);
      }
    }

    onButtonClick(event) {
      event.preventDefault();
      if (this.mode === 'add' && this.atcButton && this.atcButton.isConnected) {
        var atc = this.atcButton;
        if (atc.classList.contains('loading')) return; // request already running
        var unavailable = atc.hasAttribute('disabled') || atc.getAttribute('aria-disabled') === 'true';
        if (!unavailable) {
          // Clicking the real submit button keeps Dawn's product-form, Kaching and UpCart flows intact.
          this.lastAddClick = Date.now();
          atc.click();
          return;
        }
      }
      // Scroll mode, or the main button is unavailable: show the picker/form so the visitor sees why.
      this.scrollToTarget();
    }

    findScrollTarget() {
      var target = firstMatch([this.productRoot, document], splitList(this.dataset.scrollTarget));
      if (target && hasSize(target)) return target;
      if (this.atcButton) return this.atcButton.closest('product-form') || this.atcButton;
      return this.productRoot;
    }

    scrollToTarget(element, options) {
      var target = element || this.findScrollTarget();
      var settings = options || {};
      if (!target) return;

      var header = document.querySelector('sticky-header') ? document.querySelector('.section-header') : null;
      var headerHeight = header ? header.offsetHeight : 0;
      var barHeight = this.visible ? this.offsetHeight : 0;
      var rect = target.getBoundingClientRect();
      var available = window.innerHeight - headerHeight - barHeight;
      var top;
      if (rect.height < available - 32) {
        top = window.pageYOffset + rect.top - headerHeight - (available - rect.height) / 2;
      } else {
        top = window.pageYOffset + rect.top - headerHeight - 16;
      }

      window.scrollTo({ top: Math.max(0, Math.round(top)), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });

      // Move focus so keyboard and screen-reader users land in the picker (the bar hides itself afterwards).
      if (settings.focus !== false) {
        if (!target.hasAttribute('tabindex') && !target.matches('a[href], button, input, select, textarea')) {
          target.setAttribute('tabindex', '-1');
        }
        try {
          target.focus({ preventScroll: true });
        } catch (error) {
          /* very old browsers */
        }
      }

      if (this.highlight) {
        var previous = this.highlighted;
        if (previous && previous !== target) previous.classList.remove('ld-sticky-atc-highlight');
        this.highlighted = target;
        target.classList.add('ld-sticky-atc-highlight');
        clearTimeout(this.highlightTimer);
        this.highlightTimer = this.later(function () {
          target.classList.remove('ld-sticky-atc-highlight');
        }, 2400);
      }
    }
  }

  customElements.define('ld-sticky-atc', LdStickyAtc);
})();
